import {
  BusinessCenterOutlined,
  CheckCircleOutline,
  ExpandMore,
  LockOutlined,
  PhoneOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatBottomNav from '../components/chat/ChatBottomNav';
import AppHeader from '../components/layout/AppHeader';
import API_ENDPOINTS from '../config/api';

interface Submission {
  id: string;
  provider_name: string;
  site_name: string;
  work_date: string;
  submitted_at: string;
  expires_at: string;
  status: 'green' | 'amber' | 'red';
  question_1_text: string;
  question_2_text: string;
  question_3_text: string;
  answer_1: boolean;
  answer_2: boolean;
  answer_3: boolean;
  note_1: string;
  note_2: string;
  note_3: string;
  reference_code: string;
}

interface GoodToGoStatus {
  active_membership: {
    provider: {
      business_name: string;
    };
    site: {
      name: string;
      background_colour?: string;
      logo_url?: string | null;
      g2g_sponsor_logo_url?: string | null;
      g2g_red_message: string;
      g2g_designated_contact_name: string;
      g2g_designated_contact_role: string;
      g2g_designated_contact_phone: string;
    };
  } | null;
  questions: {
    question_1: string;
    question_2: string;
    question_3: string;
    // Reason: per-question severity drives whether a 'No' answer
    // requires a note. RED ⇒ note required (and a 'No' fails the
    // submission). AMBER ⇒ note optional (and a 'No' downgrades the
    // submission to amber). Configured per Provider in the Django
    // admin and surfaced here so the form can render the right
    // affordance without a server round-trip.
    question_1_severity: 'red' | 'amber';
    question_2_severity: 'red' | 'amber';
    question_3_severity: 'red' | 'amber';
  } | null;
  qr_payload?: string;
  current_submission: Submission | null;
  history: Submission[];
}

const FONT_FAMILY =
  'Inter, Poppins, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const statusColour = (status: Submission['status']): string => {
  if (status === 'green') return '#28A745';
  if (status === 'amber') return '#F2A900';
  return '#D64545';
};

const statusLabel = (status: Submission['status']): string =>
  status.charAt(0).toUpperCase() + status.slice(1);

const resolveMediaUrl = (url?: string | null): string | null => {
  if (!url) {
    return null;
  }
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${API_ENDPOINTS.BASE_URL}${url}`;
};

const GoodToGoPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<GoodToGoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [sponsorImageFailed, setSponsorImageFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, boolean | null>>({
    answer_1: null,
    answer_2: null,
    answer_3: null,
  });
  const [notes, setNotes] = useState<Record<string, string>>({
    note_1: '',
    note_2: '',
    note_3: '',
  });

  const loadStatus = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_ENDPOINTS.GOOD_TO_GO.STATUS);
      setData(response.data);
    } catch {
      setError('Unable to load Good To Go right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    setSponsorImageFailed(false);
  }, [data?.active_membership?.site.g2g_sponsor_logo_url]);

  const activeMembership = data?.active_membership;
  const providerAccent = activeMembership?.site.background_colour || '#9ED8FF';
  const siteLogoUrl = resolveMediaUrl(activeMembership?.site.logo_url);
  const sponsorLogoUrl = resolveMediaUrl(activeMembership?.site.g2g_sponsor_logo_url);
  // Reason: a "No" on a RED-severity question requires a non-blank note
  // before the form can be submitted. AMBER-severity questions accept a
  // "No" with no note. Mirrors the backend's GoodToGoSubmitSerializer
  // validation so the user sees the gate immediately rather than via a
  // 400 response.
  const isRedSeverityNoteMissing = (number: 1 | 2 | 3): boolean => {
    const severity = data?.questions?.[`question_${number}_severity`];
    if (severity !== 'red') return false;
    if (answers[`answer_${number}`] !== false) return false;
    return notes[`note_${number}`].trim().length === 0;
  };
  const canSubmit =
    answers.answer_1 !== null &&
    answers.answer_2 !== null &&
    answers.answer_3 !== null &&
    !isRedSeverityNoteMissing(1) &&
    !isRedSeverityNoteMissing(2) &&
    !isRedSeverityNoteMissing(3);

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) {
      setError('Please answer all three questions.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await axios.post(API_ENDPOINTS.GOOD_TO_GO.SUBMISSIONS, {
        answer_1: answers.answer_1,
        answer_2: answers.answer_2,
        answer_3: answers.answer_3,
        note_1: notes.note_1,
        note_2: notes.note_2,
        note_3: notes.note_3,
      });
      setAnswers({ answer_1: null, answer_2: null, answer_3: null });
      setNotes({ note_1: '', note_2: '', note_3: '' });
      await loadStatus();
      setFormOpen(false);
    } catch (err: any) {
      const responseData = err.response?.data;
      const firstFieldError =
        responseData && typeof responseData === 'object'
          ? Object.values(responseData).flat()[0]
          : null;
      setError(
        String(
          firstFieldError ||
          responseData?.detail ||
          'Unable to submit Good To Go.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (number: 1 | 2 | 3, text: string): React.ReactElement => {
    const answerKey = `answer_${number}`;
    const noteKey = `note_${number}`;
    const answer = answers[answerKey];
    // Reason: severity-driven note UX. Falls back to RED behaviour if
    // the backend response somehow lacks the field, matching the
    // historical hardcoded gate.
    const severity =
      data?.questions?.[`question_${number}_severity`] ?? 'red';
    const noteRequired = severity === 'red';

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, mb: 1 }}>
          {number}. {text}
        </Typography>
        <RadioGroup
          row
          value={answer === null ? '' : answer ? 'yes' : 'no'}
          onChange={(event) =>
            setAnswers((prev) => ({
              ...prev,
              [answerKey]: event.target.value === 'yes',
            }))
          }
        >
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
        {answer === false && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            required={noteRequired}
            error={noteRequired && notes[noteKey].trim().length === 0}
            helperText={
              noteRequired && notes[noteKey].trim().length === 0
                ? 'A note is required before you can submit.'
                : ' '
            }
            value={notes[noteKey]}
            onChange={(event) =>
              setNotes((prev) => ({ ...prev, [noteKey]: event.target.value }))
            }
            placeholder={
              noteRequired
                ? 'Please add a short note so your site team can help address this.'
                : 'Optional: add anything that would help your site team support you today.'
            }
            sx={{ mt: 1 }}
          />
        )}
      </Box>
    );
  };

  const renderForm = (): React.ReactElement | null => {
    if (!data?.questions) {
      return null;
    }

    return (
      <>
        {renderQuestion(1, data.questions.question_1)}
        {renderQuestion(2, data.questions.question_2)}
        {renderQuestion(3, data.questions.question_3)}
        <Button
          fullWidth
          variant="contained"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
          sx={{ borderRadius: '16px', py: 1.4, fontWeight: 900 }}
        >
          {submitting ? 'Submitting...' : 'Submit Good To Go'}
        </Button>
      </>
    );
  };

  const renderResult = (submission: Submission): React.ReactElement => {
    const isRed = submission.status === 'red';
    const phone = activeMembership?.site.g2g_designated_contact_phone;
    const expiresText = new Date(submission.expires_at).toLocaleString();
    const submittedText = new Date(submission.submitted_at).toLocaleString();
    const qrUrl = data?.qr_payload
      ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(data.qr_payload)}`
      : null;

    return (
      <Card
        sx={{
          borderRadius: '24px',
          mb: 2,
          bgcolor: statusColour(submission.status),
          color: '#FFFFFF',
          boxShadow: `0 10px 28px ${statusColour(submission.status)}55`,
        }}
      >
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
            {isRed ? (
              <WarningAmberOutlined
                sx={{ color: '#FFFFFF', fontSize: 42 }}
              />
            ) : (
              <CheckCircleOutline
                sx={{ color: '#FFFFFF', fontSize: 46 }}
              />
            )}
            <Typography variant="h4" sx={{ fontFamily: FONT_FAMILY, fontWeight: 900 }}>
              {statusLabel(submission.status)}
            </Typography>
          </Box>

          {isRed ? (
            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, mb: 2 }}>
              {activeMembership?.site.g2g_red_message}
            </Typography>
          ) : (
            <Typography sx={{ fontFamily: FONT_FAMILY, mb: 2 }}>
              Valid until <strong>{expiresText}</strong>
            </Typography>
          )}

          {qrUrl && (
            <Box
              component="img"
              src={qrUrl}
              alt="Good To Go QR code"
              sx={{
                width: 156,
                height: 156,
                bgcolor: '#FFFFFF',
                borderRadius: '16px',
                p: 1,
                mb: 2,
              }}
            />
          )}

          <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900 }}>
            {submission.reference_code}
          </Typography>

          <Box sx={{ mt: 2, textAlign: 'left', color: '#111827', bgcolor: '#FFFFFF', borderRadius: '16px', p: 2 }}>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
              Submitted: {submittedText}
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
              Expires: {expiresText}
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14, overflowWrap: 'anywhere' }}>
              QR payload: <strong>{data?.qr_payload}</strong>
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={() => setFormOpen(true)}
            sx={{
              mt: 2,
              borderRadius: '14px',
              bgcolor: '#FFFFFF',
              color: statusColour(submission.status),
              fontWeight: 900,
              '&:hover': {
                bgcolor: '#F7FAFC',
              },
            }}
          >
            Retake today's check
          </Button>

          {isRed && phone && (
            <Button
              fullWidth
              startIcon={<PhoneOutlined />}
              variant="contained"
              href={`tel:${phone}`}
              sx={{ mt: 1, borderRadius: '14px', bgcolor: '#FFFFFF', color: statusColour(submission.status), fontWeight: 900 }}
            >
              Call {activeMembership?.site.g2g_designated_contact_name || 'site contact'}
              {activeMembership?.site.g2g_designated_contact_role
                ? ` (${activeMembership.site.g2g_designated_contact_role})`
                : ''}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: 'var(--app-height, 100vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'var(--app-height, 100vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#EAF7FF',
      }}
    >
      <AppHeader title="Good To Go" backgroundColor="#2F80B7" titleColor="#FFFFFF" />

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 3 }}>
        {!activeMembership ? (
          <Card sx={{ borderRadius: '24px' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <LockOutlined sx={{ fontSize: 48, color: '#3A8DBF', mb: 2 }} />
              <Typography
                variant="h5"
                sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, mb: 1 }}
              >
                Provider members only
              </Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                Good To Go is available when your account is assigned to a Provider/Site.
              </Alert>
              <Button variant="contained" onClick={() => navigate('/profile')}>
                Go to Profile
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card
              sx={{
                borderRadius: '28px',
                border: `2px solid ${providerAccent}`,
                boxShadow: '0 12px 32px rgba(58, 141, 191, 0.22)',
                overflow: 'hidden',
                mb: 2,
              }}
            >
              <Box sx={{ bgcolor: providerAccent, px: 3, py: 2, textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontWeight: 900,
                    fontSize: 14,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#FFFFFF',
                  }}
                >
                  Daily readiness check
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '64px 1fr 96px',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {siteLogoUrl ? (
                      <Box
                        component="img"
                        src={siteLogoUrl}
                        alt={activeMembership.site.name}
                        sx={{ width: 56, height: 56, objectFit: 'contain' }}
                      />
                    ) : (
                      <BusinessCenterOutlined sx={{ fontSize: 56, color: '#3A8DBF' }} />
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: FONT_FAMILY, color: '#4A5568', fontWeight: 800 }}>
                      {activeMembership.provider.business_name} / {activeMembership.site.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 96 }}>
                    {sponsorLogoUrl && (
                      sponsorImageFailed ? (
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            color: '#3A8DBF',
                            fontSize: 10,
                            fontWeight: 900,
                            textTransform: 'uppercase',
                          }}
                        >
                          Sponsor
                        </Typography>
                      ) : (
                        <Box
                          component="img"
                          src={sponsorLogoUrl}
                          alt="Sponsor"
                          onError={() => setSponsorImageFailed(true)}
                          sx={{
                            display: 'block',
                            width: 92,
                            height: 64,
                            objectFit: 'contain',
                          }}
                        />
                      )
                    )}
                  </Box>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {data?.current_submission && renderResult(data.current_submission)}

                {!data?.current_submission && (
                  <>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, mb: 2 }}>
                      Answer today's questions
                    </Typography>
                    {renderForm()}
                  </>
                )}
              </CardContent>
            </Card>

            <Dialog
              open={formOpen}
              onClose={() => setFormOpen(false)}
              fullWidth
              maxWidth="sm"
              PaperProps={{ sx: { borderRadius: '24px' } }}
            >
              <DialogTitle sx={{ fontFamily: FONT_FAMILY, fontWeight: 900 }}>
                Retake today's check
              </DialogTitle>
              <DialogContent>{renderForm()}</DialogContent>
            </Dialog>

            {data?.history.length ? (
              <Accordion
                disableGutters
                sx={{
                  borderRadius: '24px',
                  mb: 2,
                  '&:before': { display: 'none' },
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ px: 3, py: 1.5 }}
                >
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, fontSize: 15 }}>
                    History (last 28 days)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  {data.history.map((item, index) => (
                    <Box key={item.id}>
                      {index > 0 && <Divider sx={{ my: 1.5 }} />}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, fontSize: 13 }}>
                            {new Date(item.submitted_at).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: FONT_FAMILY,
                              color: '#4A5568',
                              fontSize: 12,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={`${item.provider_name ?? ''} / ${item.site_name ?? ''}`}
                          >
                            {item.provider_name && item.site_name
                              ? `${item.provider_name} / ${item.site_name}`
                              : item.provider_name || item.site_name || item.reference_code}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            color: statusColour(item.status),
                            fontWeight: 900,
                            fontSize: 13,
                          }}
                        >
                          {statusLabel(item.status)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : null}
          </>
        )}
      </Box>

      <ChatBottomNav />
    </Box>
  );
};

export default GoodToGoPage;
